import React from 'react'
import {
  Markdown,
  Body,
  VerticalSpacer,
  Heading,
  Link,
  HorizontalSpacer,
  DecorativeShape,
  Grid,
  PageSection,
  Aside,
} from '@superhi/design'

import { Ul, WhatYoullLearnWrapper } from './styles'

import { buildPath } from 'src/routing'
import Hyperlink from 'src/components/Hyperlink'

export const ID = 'INTRO'
export const WHAT_YOULL_LEARN_ID = 'WHAT_YOULL_LEARN'
export const BETA_COURSES_ID = 'BETA_COURSES'

const MEMBERSHIP_PRODUCT_URL = buildPath('membership-product')

const VS_1 = { small: 32, medium: 40, large: 48 }
const VS_3 = { small: 16, medium: 16, large: 24 }
const VS_2 = { small: 8, medium: 8, large: 8 }

const MARKDOWN_1_OVERRIDES = {
  p: ({ children }: any) => (
    <Body level="2" maxWidth={100}>
      {children}
    </Body>
  ),
  a: ({ href, children }: any) => <Hyperlink url={href}>{children}</Hyperlink>,
}
const MARKDOWN_2_OVERRIDES = {
  p: ({ children }: any) => (
    <Body level="2" maxWidth={100}>
      {children}
    </Body>
  ),
  a: ({ href, children }: any) => <Hyperlink url={href}>{children}</Hyperlink>,
}
const MARKDOWN_2_3_OVERRIDES = {
  p: ({ children }: any) => <Body level="3">{children}</Body>,
  a: ({ href, children }: any) => <Hyperlink url={href}>{children}</Hyperlink>,
  ul: ({ children }: any) => <Ul>{children}</Ul>,
  li: ({ children }: any) => (
    <li>
      <Body level="3">{children}</Body>
    </li>
  ),
}

type Props = {
  description: string
  requirements: string
  targetAudience: string
  isBeta: boolean
  overview: string[]
  contents: {
    title: string
    description?: string | null
  }[]
} & Pick<React.ComponentProps<typeof PageSection>, 'noBottomPadding'>

const Intro: React.FC<React.PropsWithChildren<Props>> = ({
  isBeta,
  description,
  requirements,
  targetAudience,
  overview,
  contents,
  noBottomPadding,
}) => (
  <PageSection id={ID} noBottomPadding={noBottomPadding}>
    <Grid rowGap={VS_1}>
      <Grid.Cell span={{ small: 'full', medium: 'full', large: 7 }}>
        <VerticalSpacer amount={VS_1}>
          <Markdown overrides={MARKDOWN_1_OVERRIDES}>{description}</Markdown>

          <VerticalSpacer id={WHAT_YOULL_LEARN_ID} amount={VS_3}>
            <Heading level="3" color="BLUE_50" as={'h2'}>
              What you&rsquo;ll learn
            </Heading>

            <WhatYoullLearnWrapper amount={VS_2}>
              {overview.map((overviewItem, i) => (
                <HorizontalSpacer key={i} amount={{ small: 8, medium: 16, large: 16 }}>
                  <DecorativeShape
                    name={DecorativeShape.names[i % DecorativeShape.names.length]}
                    size={{ small: 40, medium: 40, large: 40 }}
                  />
                  <Markdown overrides={MARKDOWN_2_OVERRIDES}>{overviewItem}</Markdown>
                </HorizontalSpacer>
              ))}
            </WhatYoullLearnWrapper>
          </VerticalSpacer>
        </VerticalSpacer>
      </Grid.Cell>

      <Grid.Cell span={{ small: 'full', medium: 'full', large: 5 }}>
        <VerticalSpacer amount={VS_1}>
          <Aside label="What you’ll get" labelColor="BLUE_50" backgroundColor="PALE_BLUE_40">
            {contents.map((contentItem, i) => (
              <Body level="3" key={i}>
                <strong>{contentItem.title}</strong>
                <span>{contentItem.description && ` – ${contentItem.description}`}</span>
              </Body>
            ))}
          </Aside>

          {isBeta && (
            <div id={BETA_COURSES_ID}>
              <Aside label="This is a beta course" labelColor="BLUE_50">
                <Body level="3">
                  <span>With a </span>
                  <Link href={MEMBERSHIP_PRODUCT_URL}>SuperHi Unlimited membership </Link>
                  <span>
                    you get exclusive early access to selected courses before we release them to a
                    wider audience. These courses are released in stages, and your feedback will
                    help us shape and refine them to provide you with a better learning experience.
                  </span>
                </Body>
              </Aside>
            </div>
          )}

          <Aside label="Who this course is for" labelColor="BLUE_50">
            <Markdown overrides={MARKDOWN_2_3_OVERRIDES}>{targetAudience}</Markdown>
          </Aside>

          <Aside label="What you’ll need" labelColor="BLUE_50">
            <Markdown overrides={MARKDOWN_2_3_OVERRIDES}>{requirements}</Markdown>
          </Aside>
        </VerticalSpacer>
      </Grid.Cell>
    </Grid>
  </PageSection>
)

export default Intro
